import React, { useState, useEffect, useRef } from 'react';
import PhotoViewer from './PhotoViewer';
import './EquipmentCallout.css';

const EquipmentCallout = ({ equipment, mapCenter, mapZoom, mapContainer, onClose }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [calloutPosition, setCalloutPosition] = useState('top-right');
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const calloutRef = useRef(null);

  useEffect(() => {
    if (!equipment.lngLat || !mapContainer) return;

    const updatePosition = () => {
      const container = mapContainer.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const [lng, lat] = equipment.lngLat;
      const [centerLng, centerLat] = mapCenter;

      // Convert lat/lng to pixel coordinates (simplified)
      const latDiff = lat - centerLat;
      const lngDiff = lng - centerLng;
      const pixelsPerDegree = Math.pow(2, mapZoom) * 256 / 360;
      
      const x = (lngDiff * pixelsPerDegree) + (rect.width / 2);
      const y = (rect.height / 2) - (latDiff * pixelsPerDegree);

      // Determine callout position to avoid overlapping
      let calloutPos = 'top-right';
      const calloutWidth = 200;
      const calloutHeight = 120;
      const margin = 20;

      // Check if callout would go off screen
      if (x + calloutWidth + margin > rect.width) {
        calloutPos = 'top-left';
      }
      if (y - calloutHeight - margin < 0) {
        calloutPos = calloutPos === 'top-left' ? 'bottom-left' : 'bottom-right';
      }

      setCalloutPosition(calloutPos);
      setPosition({ x, y });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [equipment.lngLat, mapCenter, mapZoom, mapContainer]);

  const getCalloutStyle = () => {
    const baseStyle = {
      position: 'absolute',
      zIndex: 1000,
      pointerEvents: 'auto'
    };

    switch (calloutPosition) {
      case 'top-right':
        return {
          ...baseStyle,
          left: `${position.x + 15}px`,
          top: `${position.y - 130}px`
        };
      case 'top-left':
        return {
          ...baseStyle,
          left: `${position.x - 215}px`,
          top: `${position.y - 130}px`
        };
      case 'bottom-right':
        return {
          ...baseStyle,
          left: `${position.x + 15}px`,
          top: `${position.y + 15}px`
        };
      case 'bottom-left':
        return {
          ...baseStyle,
          left: `${position.x - 215}px`,
          top: `${position.y + 15}px`
        };
      default:
        return baseStyle;
    }
  };

  const getArrowStyle = () => {
    const arrowSize = 10;
    const baseStyle = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderStyle: 'solid'
    };

    switch (calloutPosition) {
      case 'top-right':
        return {
          ...baseStyle,
          left: '-10px',
          top: '20px',
          borderWidth: `${arrowSize}px ${arrowSize}px ${arrowSize}px 0`,
          borderColor: 'transparent #ffffff transparent transparent'
        };
      case 'top-left':
        return {
          ...baseStyle,
          right: '-10px',
          top: '20px',
          borderWidth: `${arrowSize}px 0 ${arrowSize}px ${arrowSize}px`,
          borderColor: 'transparent transparent transparent #ffffff'
        };
      case 'bottom-right':
        return {
          ...baseStyle,
          left: '-10px',
          bottom: '20px',
          borderWidth: `${arrowSize}px ${arrowSize}px ${arrowSize}px 0`,
          borderColor: 'transparent #ffffff transparent transparent'
        };
      case 'bottom-left':
        return {
          ...baseStyle,
          right: '-10px',
          bottom: '20px',
          borderWidth: `${arrowSize}px 0 ${arrowSize}px ${arrowSize}px`,
          borderColor: 'transparent transparent transparent #ffffff'
        };
      default:
        return baseStyle;
    }
  };

  const handlePhotoDelete = (index) => {
    // This will be handled by the parent component when we implement photo management
    console.log('Photo delete requested for index:', index);
  };

  if (!equipment.lngLat) return null;

  return (
    <>
      <div className="equipment-callout" style={getCalloutStyle()} ref={calloutRef}>
        <div className="callout-arrow" style={getArrowStyle()}></div>
        <div className="callout-content">
          <div className="callout-header">
            <h4>{equipment.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
            <button className="callout-close" onClick={onClose}>×</button>
          </div>
          <div className="callout-body">
            {equipment.details && Object.keys(equipment.details).length > 0 && (
              <div className="callout-details">
                {Object.entries(equipment.details).map(([key, value]) => (
                  <div key={key} className="detail-item">
                    <span className="detail-label">{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</span>
                    <span className="detail-value">{value}</span>
                  </div>
                ))}
              </div>
            )}
            {equipment.notes && (
              <div className="callout-notes">
                <strong>Notes:</strong> {equipment.notes}
              </div>
            )}
            
            {/* Photo Button */}
            {equipment.photos && equipment.photos.length > 0 && (
              <div className="callout-photos">
                <button 
                  className="view-photos-button"
                  onClick={() => setShowPhotoViewer(true)}
                >
                  Photos ({equipment.photos.length})
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Photo Viewer Modal */}
      {showPhotoViewer && equipment.photos && (
        <PhotoViewer
          photos={equipment.photos}
          onClose={() => setShowPhotoViewer(false)}
          onDelete={handlePhotoDelete}
        />
      )}
    </>
  );
};

export default EquipmentCallout; 